import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface HelpTooltipProps {
  /** The help text to display when clicked */
  helpText: string;
  /** Callback when the help icon is clicked (for tracking interactions) */
  onHelpViewed?: () => void;
  /** Size variant - 'sm' for inline with small text, 'md' for normal */
  size?: 'sm' | 'md';
  /** Optional className for the container */
  className?: string;
}

/**
 * Calculate tooltip position centered on content area
 */
function calculateTooltipPosition(tooltipEl: HTMLDivElement): { left: number; top: number } {
  const tooltipHeight = tooltipEl.offsetHeight;
  const tooltipWidth = tooltipEl.offsetWidth;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const quarterFold = viewportHeight * 0.25;
  
  // Detect sidebar width dynamically
  let sidebarWidth = 0;
  const sidebar = document.querySelector('aside.sidebar');
  if (sidebar) {
    const sidebarRect = sidebar.getBoundingClientRect();
    if (sidebarRect.right > 0) {
      sidebarWidth = sidebarRect.width;
    }
  }
  
  // On mobile (< 1024px / lg breakpoint), sidebar is overlay, so don't offset
  const isDesktop = viewportWidth >= 1024;
  const effectiveSidebarWidth = isDesktop ? sidebarWidth : 0;
  
  // Calculate horizontal center of content area (viewport minus sidebar)
  const contentWidth = viewportWidth - effectiveSidebarWidth;
  const contentCenterX = effectiveSidebarWidth + (contentWidth / 2);
  const left = contentCenterX - (tooltipWidth / 2);
  
  // Calculate vertical position - center but not below 25vh
  const centeredTop = (viewportHeight / 2) - (tooltipHeight / 2);
  const top = Math.min(centeredTop, quarterFold);
  
  return { left, top };
}

/**
 * Shared tooltip popup component with smart positioning:
 * - Horizontally centered relative to main content area (excluding sidebar)
 * - Vertically: centered if possible, otherwise top at 25vh
 * Uses React Portal to render at document body level, escaping any parent CSS transforms
 * Non-interactive version (pointer-events-none) for click-triggered tooltips
 */
interface TooltipPopupProps {
  helpText: string;
}

export const TooltipPopup: React.FC<TooltipPopupProps> = ({ helpText }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!tooltipRef.current) return;
    setPosition(calculateTooltipPosition(tooltipRef.current));
  }, [helpText]);

  const tooltipElement = (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={position ? { left: position.left, top: position.top } : undefined}
      className={`
        fixed z-[9999]
        w-[85vw] max-w-md
        px-4 py-3
        bg-popover text-popover-foreground
        text-sm leading-relaxed text-left
        rounded-lg shadow-xl
        border border-border
        animate-in fade-in-0 zoom-in-95 duration-150
        whitespace-pre-line
        pointer-events-none
        max-h-[65vh] overflow-y-auto
        ${!position ? 'opacity-0' : ''}
      `}
    >
      {helpText}
    </div>
  );

  return createPortal(tooltipElement, document.body);
};

/**
 * Interactive tooltip popup for hover-triggered tooltips
 * - Has pointer-events-auto so it can receive mouse events
 * - Reports mouse enter/leave to parent for hover bridge behavior
 * - Reports whether content is scrollable
 */
interface InteractiveTooltipPopupProps {
  helpText: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onScrollableChange?: (isScrollable: boolean) => void;
}

export const InteractiveTooltipPopup: React.FC<InteractiveTooltipPopupProps> = ({ 
  helpText, 
  onMouseEnter, 
  onMouseLeave,
  onScrollableChange 
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!tooltipRef.current) return;
    setPosition(calculateTooltipPosition(tooltipRef.current));
    
    // Check if content is scrollable
    const el = tooltipRef.current;
    const isScrollable = el.scrollHeight > el.clientHeight;
    onScrollableChange?.(isScrollable);
  }, [helpText, onScrollableChange]);

  const tooltipElement = (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={position ? { left: position.left, top: position.top } : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`
        fixed z-[9999]
        w-[85vw] max-w-md
        px-4 py-3
        bg-popover text-popover-foreground
        text-sm leading-relaxed text-left
        rounded-lg shadow-xl
        border border-border
        animate-in fade-in-0 zoom-in-95 duration-150
        whitespace-pre-line
        pointer-events-auto
        max-h-[65vh] overflow-y-auto
        ${!position ? 'opacity-0' : ''}
      `}
    >
      {helpText}
    </div>
  );

  return createPortal(tooltipElement, document.body);
};

/**
 * HelpTooltip - A question mark icon in a circle that shows help text on click
 * 
 * Behavior:
 * - Click on (?) to show tooltip centered on screen
 * - Click anywhere to dismiss
 * - Escape key dismisses tooltip
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  helpText,
  onHelpViewed,
  size = 'sm',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close tooltip
  useEffect(() => {
    if (!isOpen) return;

    const handleClickAnywhere = () => {
      setIsOpen(false);
      onHelpViewed?.();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        onHelpViewed?.();
      }
    };

    // Add listeners with a small delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickAnywhere);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickAnywhere);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onHelpViewed]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      onHelpViewed?.();
    }
  }, [isOpen, onHelpViewed]);

  // Size-based styling
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5 text-[10px]' : 'w-4 h-4 text-xs';
  
  return (
    <div 
      ref={containerRef}
      className={`relative inline-flex items-center ${className}`}
    >
      {/* Question mark icon button */}
      <button
        type="button"
        onClick={handleClick}
        className={`
          ${iconSize}
          rounded-full border border-muted-foreground/50
          text-muted-foreground hover:text-foreground hover:border-foreground
          flex items-center justify-center
          transition-colors duration-150
          focus:outline-none focus:ring-1 focus:ring-ring
          cursor-help
        `}
        aria-label="Show help"
        aria-expanded={isOpen}
      >
        <span className="font-medium leading-none">?</span>
      </button>

      {/* Tooltip popup - rendered via portal for proper positioning */}
      {isOpen && <TooltipPopup helpText={helpText} />}
    </div>
  );
};

/**
 * Variant for use inside buttons - prevents event bubbling to parent button
 */
interface InlineHelpTooltipProps extends Omit<HelpTooltipProps, 'size'> {
  /** Prevent click from triggering parent button action */
  preventParentAction?: boolean;
}

export const InlineHelpTooltip: React.FC<InlineHelpTooltipProps> = ({
  helpText,
  onHelpViewed,
  className = '',
  preventParentAction = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Handle click anywhere to close tooltip
  useEffect(() => {
    if (!isOpen) return;

    const handleClickAnywhere = () => {
      setIsOpen(false);
      onHelpViewed?.();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        onHelpViewed?.();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickAnywhere);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickAnywhere);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onHelpViewed]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (preventParentAction) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      onHelpViewed?.();
    }
  }, [isOpen, onHelpViewed, preventParentAction]);

  return (
    <span 
      ref={containerRef}
      className={`relative inline-flex items-center ${className}`}
    >
      {/* Question mark icon - uses span instead of button to work inside buttons */}
      <span
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        className={`
          w-3 h-3 text-[9px]
          rounded-full border border-current/50
          text-muted-foreground hover:text-foreground
          inline-flex items-center justify-center
          transition-colors duration-150
          cursor-help
          ml-1
        `}
        aria-label="Show help"
        aria-expanded={isOpen}
      >
        <span className="font-medium leading-none">?</span>
      </span>

      {/* Tooltip popup - rendered via portal for proper positioning */}
      {isOpen && <TooltipPopup helpText={helpText} />}
    </span>
  );
};

/**
 * HoverHelpTooltip - Wraps children and shows tooltip on hover (desktop) or long-press (mobile)
 * 
 * Use this for controls that have already had their questionmark dismissed,
 * allowing users to still access help text through hover/long-press.
 * Tooltip displays centered on screen for consistency.
 * 
 * Features hover bridge: when mouse leaves trigger, there's a grace period to move to the tooltip.
 * If content is scrollable, tooltip stays open while mouse is over it.
 */
interface HoverHelpTooltipProps {
  /** The help text to display */
  helpText: string;
  /** Children to wrap */
  children: React.ReactNode;
  /** Whether to show the tooltip (typically when questionmark is hidden but help exists) */
  enabled?: boolean;
  /** Optional className for the wrapper */
  className?: string;
  /** Hover delay in ms before showing tooltip (default: 800ms) */
  hoverDelay?: number;
  /** Long-press duration in ms for mobile (default: 500ms) */
  longPressDelay?: number;
  /** Max pixels mouse can move during hover delay and still show tooltip (default: 8px) */
  stillnessThreshold?: number;
  /** Grace period in ms to move from trigger to tooltip (default: 300ms) */
  gracePeriod?: number;
}

export const HoverHelpTooltip: React.FC<HoverHelpTooltipProps> = ({
  helpText,
  children,
  enabled = true,
  className = '',
  hoverDelay = 800,
  longPressDelay = 500,
  stillnessThreshold = 8,
  gracePeriod = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const mouseStartRef = useRef<{ x: number; y: number } | null>(null);
  const isStillRef = useRef<boolean>(true);
  const isMouseOverTriggerRef = useRef<boolean>(false);
  const isMouseOverTooltipRef = useRef<boolean>(false);
  const isScrollableRef = useRef<boolean>(false);

  // Clear all timeouts helper
  const clearAllTimeouts = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => clearAllTimeouts();
  }, [clearAllTimeouts]);

  // Close tooltip only if mouse is not over trigger or tooltip
  const scheduleClose = useCallback(() => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    // If scrollable content, use longer grace period
    const effectiveGracePeriod = isScrollableRef.current ? gracePeriod * 2 : gracePeriod;

    closeTimeoutRef.current = setTimeout(() => {
      // Only close if mouse is not over trigger AND not over tooltip
      if (!isMouseOverTriggerRef.current && !isMouseOverTooltipRef.current) {
        setIsOpen(false);
      }
    }, effectiveGracePeriod);
  }, [gracePeriod]);

  // Cancel scheduled close (mouse re-entered trigger or tooltip)
  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Tooltip mouse enter - cancel any pending close
  const handleTooltipMouseEnter = useCallback(() => {
    isMouseOverTooltipRef.current = true;
    cancelClose();
  }, [cancelClose]);

  // Tooltip mouse leave - schedule close with grace period
  const handleTooltipMouseLeave = useCallback(() => {
    isMouseOverTooltipRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  // Track scrollability changes
  const handleScrollableChange = useCallback((scrollable: boolean) => {
    isScrollableRef.current = scrollable;
  }, []);

  // Desktop: Mouse enter with delay - record starting position
  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    
    isMouseOverTriggerRef.current = true;
    cancelClose(); // Cancel any pending close from previous leave
    
    mouseStartRef.current = { x: e.clientX, y: e.clientY };
    isStillRef.current = true;
    
    // Only start showing if not already open
    if (!isOpen) {
      hoverTimeoutRef.current = setTimeout(() => {
        if (isStillRef.current) {
          setIsOpen(true);
        }
      }, hoverDelay);
    }
  }, [enabled, hoverDelay, isOpen, cancelClose]);

  // Desktop: Mouse move - check if mouse has moved too far (not still)
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!mouseStartRef.current || !enabled) return;
    
    const dx = Math.abs(e.clientX - mouseStartRef.current.x);
    const dy = Math.abs(e.clientY - mouseStartRef.current.y);
    
    if (dx > stillnessThreshold || dy > stillnessThreshold) {
      isStillRef.current = false;
      mouseStartRef.current = { x: e.clientX, y: e.clientY };
      
      // If tooltip is open and content is scrollable, don't close on movement
      if (isOpen && !isScrollableRef.current) {
        setIsOpen(false);
      }
      
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      
      // Only restart hover timer if tooltip is not already open
      if (!isOpen) {
        hoverTimeoutRef.current = setTimeout(() => {
          if (isStillRef.current) {
            setIsOpen(true);
          }
        }, hoverDelay);
      }
      
      isStillRef.current = true;
    }
  }, [enabled, stillnessThreshold, hoverDelay, isOpen]);

  // Desktop: Mouse leave - schedule close with grace period (hover bridge)
  const handleMouseLeave = useCallback(() => {
    isMouseOverTriggerRef.current = false;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    mouseStartRef.current = null;
    isStillRef.current = true;
    
    // Schedule close with grace period instead of immediate close
    if (isOpen) {
      scheduleClose();
    }
  }, [isOpen, scheduleClose]);

  // Cancel tooltip on mousedown - user is interacting (clicking)
  const handleMouseDown = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    mouseStartRef.current = null;
    isStillRef.current = true;
    
    // For scrollable content, don't close on click (user might be scrolling)
    // For non-scrollable, close immediately
    if (isOpen && !isScrollableRef.current) {
      cancelClose();
      setIsOpen(false);
    }
  }, [isOpen, cancelClose]);

  // Mobile: Touch start - begin long press timer
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    
    longPressTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, longPressDelay);
  }, [enabled, longPressDelay]);

  // Mobile: Touch move - cancel if moved too far
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current || !longPressTimeoutRef.current) return;
    
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    
    if (dx > 10 || dy > 10) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
      touchStartRef.current = null;
    }
  }, []);

  // Mobile: Touch end - for scrollable content, keep open longer
  const handleTouchEnd = useCallback(() => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    touchStartRef.current = null;
    
    // For scrollable content, give more time before closing
    const delay = isScrollableRef.current ? 2000 : 100;
    setTimeout(() => {
      setIsOpen(false);
    }, delay);
  }, []);

  // Click outside handler for scrollable tooltips
  useEffect(() => {
    if (!isOpen || !isScrollableRef.current) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is outside both trigger and tooltip
      const target = e.target as Node;
      const isOutsideTrigger = containerRef.current && !containerRef.current.contains(target);
      
      // Check if click is on the tooltip (which is in a portal)
      const tooltipEl = document.querySelector('[role="tooltip"]');
      const isOutsideTooltip = !tooltipEl || !tooltipEl.contains(target);
      
      if (isOutsideTrigger && isOutsideTooltip) {
        setIsOpen(false);
      }
    };

    // Small delay to prevent immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {children}
      
      {/* Interactive tooltip popup with hover bridge support */}
      {isOpen && (
        <InteractiveTooltipPopup 
          helpText={helpText}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onScrollableChange={handleScrollableChange}
        />
      )}
    </div>
  );
};
